/**
 * 高性能实体列表管理器
 * 管理场景中的所有实体，支持快速查找和批量操作
 */
export class EntityList {
    get count() {
        return this.buffer.length;
    }
    constructor(scene) {
        this.buffer = [];
        // 索引映射，提升查找性能
        this._idToEntity = new Map();
        this._nameToEntities = new Map();
        // 延迟操作队列
        this._entitiesToAdd = [];
        this._entitiesToRemove = [];
        this._scene = scene;
    }
    /**
     * 添加实体
     * @param entity 要添加的实体
     */
    add(entity) {
        this.addImmediate(entity);
    }
    /**
     * 立即添加实体
     * @param entity 要添加的实体
     */
    addImmediate(entity) {
        // 检查是否已存在
        if (this._idToEntity.has(entity.id)) {
            return;
        }
        this.buffer.push(entity);
        this._idToEntity.set(entity.id, entity);
        // 更新名称索引
        this.updateNameIndex(entity, true);
    }
    /**
     * 移除实体
     * @param entity 要移除的实体
     */
    remove(entity) {
        this.removeImmediate(entity);
    }
    /**
     * 立即移除实体
     * @param entity 要移除的实体
     */
    removeImmediate(entity) {
        const index = this.buffer.indexOf(entity);
        if (index !== -1) {
            this.buffer.splice(index, 1);
            this._idToEntity.delete(entity.id);
            // 更新名称索引
            this.updateNameIndex(entity, false);
            // 回收实体ID到ID池
            if (this._scene && this._scene.identifierPool) {
                this._scene.identifierPool.checkIn(entity.id);
            }
        }
    }
    /**
     * 移除所有实体
     * Remove all entities
     *
     * 包括 buffer 中的实体和待添加队列中的实体。
     * Includes entities in buffer and entities in pending add queue.
     */
    removeAllEntities() {
        const idsToRecycle = [];
        // 销毁 buffer 中的实体
        // Destroy entities in buffer
        for (let i = this.buffer.length - 1; i >= 0; i--) {
            idsToRecycle.push(this.buffer[i].id);
            this.buffer[i].destroy();
        }
        // 销毁待添加队列中的实体（这些实体已创建但尚未加入 buffer）
        // Destroy entities in pending add queue (created but not yet in buffer)
        for (const entity of this._entitiesToAdd) {
            idsToRecycle.push(entity.id);
            entity.destroy();
        }
        // 批量回收 ID
        // Recycle IDs in batch
        if (this._scene && this._scene.identifierPool) {
            for (const id of idsToRecycle) {
                this._scene.identifierPool.checkIn(id);
            }
        }
        this.buffer.length = 0;
        this._idToEntity.clear();
        this._nameToEntities.clear();
        this._entitiesToAdd.length = 0;
        this._entitiesToRemove.length = 0;
    }
    /**
     * 更新实体列表，处理延迟操作
     */
    updateLists() {
        // 处理延迟添加的实体
        if (this._entitiesToAdd.length > 0) {
            for (const entity of this._entitiesToAdd) {
                this.addImmediate(entity);
            }
            this._entitiesToAdd.length = 0;
        }
        // 处理延迟移除的实体
        if (this._entitiesToRemove.length > 0) {
            for (const entity of this._entitiesToRemove) {
                this.removeImmediate(entity);
            }
            this._entitiesToRemove.length = 0;
        }
    }
    /**
     * 更新实体列表
     *
     * 处理延迟操作（添加/删除实体）
     */
    update() {
        // 处理延迟操作
        this.updateLists();
    }
    /**
     * 根据名称查找实体（使用索引，O(1)复杂度）
     * @param name 实体名称
     * @returns 找到的第一个实体或null
     */
    findEntity(name) {
        const entities = this._nameToEntities.get(name);
        return entities && entities.length > 0 ? entities[0] : null;
    }
    /**
     * 根据名称查找所有实体
     * @param name 实体名称
     * @returns 找到的所有实体数组
     */
    findEntitiesByName(name) {
        return this._nameToEntities.get(name) || [];
    }
    /**
     * 根据ID查找实体（使用索引，O(1)复杂度）
     * @param id 实体ID
     * @returns 找到的实体或null
     */
    findEntityById(id) {
        return this._idToEntity.get(id) || null;
    }
    /**
     * 根据标签查找实体
     * @param tag 标签
     * @returns 找到的所有实体数组
     */
    findEntitiesByTag(tag) {
        const result = [];
        for (const entity of this.buffer) {
            if (entity.tag === tag) {
                result.push(entity);
            }
        }
        return result;
    }
    /**
     * 根据组件类型查找实体
     * @param componentType 组件类型
     * @returns 找到的所有实体数组
     */
    findEntitiesWithComponent(componentType) {
        const result = [];
        for (const entity of this.buffer) {
            if (entity.hasComponent(componentType)) {
                result.push(entity);
            }
        }
        return result;
    }
    /**
     * 批量操作：对所有实体执行指定操作
     * @param action 要执行的操作
     */
    forEach(action) {
        for (const entity of this.buffer) {
            action(entity);
        }
    }
    /**
     * 批量操作：对符合条件的实体执行指定操作
     * @param predicate 筛选条件
     * @param action 要执行的操作
     */
    forEachWhere(predicate, action) {
        for (const entity of this.buffer) {
            if (predicate(entity)) {
                action(entity);
            }
        }
    }
    /**
     * 更新名称索引
     * @param entity 实体
     * @param isAdd 是否为添加操作
     */
    updateNameIndex(entity, isAdd) {
        if (!entity.name) {
            return;
        }
        if (isAdd) {
            let entities = this._nameToEntities.get(entity.name);
            if (!entities) {
                entities = [];
                this._nameToEntities.set(entity.name, entities);
            }
            entities.push(entity);
        }
        else {
            const entities = this._nameToEntities.get(entity.name);
            if (entities) {
                const index = entities.indexOf(entity);
                if (index !== -1) {
                    entities.splice(index, 1);
                    // 如果数组为空，删除映射
                    if (entities.length === 0) {
                        this._nameToEntities.delete(entity.name);
                    }
                }
            }
        }
    }
    /**
     * 重新排序实体
     * @param entityId 要移动的实体ID
     * @param newIndex 新的索引位置
     */
    reorderEntity(entityId, newIndex) {
        const entity = this._idToEntity.get(entityId);
        if (!entity)
            return;
        const currentIndex = this.buffer.indexOf(entity);
        if (currentIndex === -1 || currentIndex === newIndex)
            return;
        // 限制索引范围
        const clampedIndex = Math.max(0, Math.min(newIndex, this.buffer.length - 1));
        // 从当前位置移除
        this.buffer.splice(currentIndex, 1);
        // 插入到新位置
        this.buffer.splice(clampedIndex, 0, entity);
    }
    /**
     * 获取实体列表的统计信息
     * @returns 统计信息
     */
    getStats() {
        let activeCount = 0;
        for (const entity of this.buffer) {
            if (entity.enabled && !entity.isDestroyed) {
                activeCount++;
            }
        }
        return {
            totalEntities: this.buffer.length,
            activeEntities: activeCount,
            pendingAdd: this._entitiesToAdd.length,
            pendingRemove: this._entitiesToRemove.length,
            nameIndexSize: this._nameToEntities.size
        };
    }
}
//# sourceMappingURL=EntityList.js.map